import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactElement, useEffect } from 'react';
import useTransition from 'react-transition-state';
import { SearchResultsMenuItem } from '.';
import { SearchQueryFilter, SearchResult } from '../types/search-result';
import classNames from '../utilities/class-names';
import {
  SearchResultGroup,
  searchResultTypeLabel,
} from '../utilities/search-results';
import './SearchResultsMenu.scss';

type SearchResultsMenuProps = {
  id: string;
  groups: SearchResultGroup[];
  filters: SearchQueryFilter[];
  terms?: string[];
  activeIndex: number;
  show?: boolean;
  onSelect?: () => void;
  onHover?: (index: number) => void;
  onRemoveFilter?: (filter: SearchQueryFilter) => void;
  onFilterByType?: (type: SearchResult['type']) => void;
};

export const searchResultsMenuItemId = (menuId: string, index: number) =>
  `${menuId}-item-${index}`;

function filterLabel(filter: SearchQueryFilter): string {
  const [values, invalidLabel] =
    filter.key === 'type'
      ? [filter.values.map(searchResultTypeLabel), 'unknown type']
      : [filter.values.map(tag => `#${tag}`), 'invalid tag'];

  return [
    // Tags from the same filter can match any of them
    values.join(filter.key === 'tag' ? ' or ' : ', '),
    ...filter.invalid.map(value => `${invalidLabel} "${value}"`),
  ]
    .filter(Boolean)
    .join(', ');
}

export function SearchResultsMenu({
  id,
  groups,
  filters,
  terms = [],
  activeIndex,
  show,
  onSelect,
  onHover,
  onRemoveFilter,
  onFilterByType,
}: SearchResultsMenuProps): ReactElement {
  const [state, toggle] = useTransition({
    timeout: 200,
    preEnter: true,
    initialEntered: show,
  });

  useEffect(() => {
    toggle(show);
  }, [toggle, show]);

  const hasTypeFilter = filters.some(filter => filter.key === 'type');

  // Results are numbered in display order, so that the active index matches
  // what the user sees when moving through them with the arrow keys
  let index = 0;

  return (
    <>
      <div
        id={id}
        role="listbox"
        className={classNames('search-results-menu', state.status)}
      >
        <div className="search-results-menu-arrow"></div>
        <div className="search-results-menu-content">
          {filters.length > 0 && (
            <div className="search-results-menu-filters">
              {filters.map(filter => (
                <button
                  key={filter.raw}
                  type="button"
                  tabIndex={-1}
                  className={classNames(
                    'search-results-menu-filter',
                    filter.invalid.length > 0 ? 'invalid' : undefined
                  )}
                  title={`Remove ${filter.raw}`}
                  onClick={() => onRemoveFilter?.(filter)}
                >
                  <span className="search-results-menu-filter-key">
                    {filter.key}
                  </span>
                  {filterLabel(filter)}
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              ))}
            </div>
          )}
          {groups.length === 0 && (
            <div className="search-results-menu-empty">No results</div>
          )}
          {groups.map(group => (
            <div
              key={group.type}
              role="group"
              aria-label={group.heading}
              className="search-results-menu-group"
            >
              <div className="search-results-menu-group-heading">
                <span>{group.heading}</span>
                {!hasTypeFilter && (
                  <button
                    type="button"
                    tabIndex={-1}
                    className="search-results-menu-group-filter"
                    title={`Only show ${group.heading.toLowerCase()}`}
                    onClick={() => onFilterByType?.(group.type)}
                  >
                    type:{group.type}
                  </button>
                )}
              </div>
              {group.results.map(result => {
                const i = index++;
                return (
                  <SearchResultsMenuItem
                    key={i}
                    id={searchResultsMenuItemId(id, i)}
                    item={result}
                    terms={terms}
                    active={i === activeIndex}
                    onSelect={onSelect}
                    onHover={() => onHover?.(i)}
                  />
                );
              })}
            </div>
          ))}
        </div>
        <div className="search-results-menu-footer">
          <span>
            {hasTypeFilter
              ? 'Click a filter to remove it'
              : 'Narrow your search with type:list, type:token, #tag…'}
          </span>
          <span>↑↓ to move · Enter to open · Esc to close</span>
        </div>
      </div>
    </>
  );
}
